import { addMinutes, setMilliseconds, setSeconds } from 'date-fns'

// Web Crypto API — works in both Node (19+) and Vite SSR bundle
function secureRandomInt(max: number): number {
  const array = new Uint32Array(1)
  globalThis.crypto.getRandomValues(array)
  return array[0] % max
}

export function generatePin(length: number): string {
  const digits = '0123456789'
  return Array.from(
    { length },
    () => digits[secureRandomInt(digits.length)],
  ).join('')
}

export function generateCode(numberOfChars: number): string {
  const digits = '0123456789'
  return Array.from(
    { length: numberOfChars },
    () => digits[secureRandomInt(digits.length)],
  ).join('')
}

/**
 * This function is making sure that we don't send every event to the SQS queue but instead we wait for at least 3 minutes.
 * When end user scans the code it is highly possible that they will either go through all the steps in the next few minutes or they
 * will stay at the first step for a while.
 * We set the time to finish to 3 minutes and we round to the nearest 5 minutes that are at least 3 minutes ahead of time.
 *
 * Examples:
 * If the user starts filling in the form at 11:00 the event will be sent at 11:05 (3 minutes tolerance and round to the next 5)
 * If the user starts filling in the form at 11:03 the event will be sent at 11:10 (3 minutes tolerance and round to the next 5)
 * If the user starts filling in the form at 11:02 the event will be sent at 11:10 (3 minutes tolerance and round to the next 5)
 * */
export function roundToNext5Min(date: Date = new Date()) {
  const minutes = date.getMinutes()
  const diff = 5 - (minutes % 5) // how many minutes left to the nearest 5
  const tolerance = diff < 4 ? 5 : 0 // the given time for user to finish the steps

  return setSeconds(setMilliseconds(addMinutes(date, diff + tolerance), 0), 0)
}

export const getEndUserPreviewData = (isReferralUserPreview: boolean) => {
  return {
    id: 'b15342f0-a5d6-4187-a5b1-e73cba228f66',
    firstName: isReferralUserPreview ? '' : 'Vincent',
    middle: 'van',
    lastName: 'Gogh',
    email: 'vincent@example.com',
    address: {
      city: 'Amsterdam',
      country: 'The Netherlands',
      postcode: '1234AB',
      streetName: 'Windmolenstraat 10',
      houseNumber: '4a',
    },
    birthday: '1993-06-27',
    campaignIncentiveId: null,
    phoneNumber: '1234567890',
    sku: 'AA00A00',
    digitalCode: 'code',
    uniqueCode: isReferralUserPreview ? 'referral-user' : 'end-user',
    referralOf: isReferralUserPreview
      ? 'b15342f0-a5d6-4187-a5b1-e73cba228f66'
      : null,
  }
}

export function obfuscateString(str: string, last: number = 4) {
  return `${str.slice(0, last * -1)}${str.slice(last * -1).replace(/./g, '*')}`
}

export const getNameFormat = (
  nameFormat: string,
  endUser: {
    firstName: string
    middle?: string | null
    lastName?: string | null
  },
) => {
  switch (nameFormat) {
    case 'firstName':
      return endUser.firstName
    case 'lastName':
      return [endUser.middle, endUser.lastName].filter(Boolean).join(' ')
    case 'fullName':
      return [endUser.firstName, endUser.middle, endUser.lastName]
        .filter(Boolean)
        .join(' ')
    default:
      return ''
  }
}

export function resolveDisplayName(
  salutation: Record<string, unknown> | null | undefined,
  endUser: {
    firstName: string
    middle?: string | null
    lastName?: string | null
  },
): string {
  const nameFormat = salutation?.['nameFormat'] as string | undefined

  if (nameFormat) {
    return getNameFormat(nameFormat, endUser)
  }

  return endUser.firstName || ''
}

export function formatNameForEmail(name: string): string {
  const trimmed = name.trim()
  return trimmed ? ` ${trimmed},` : ','
}

export function formatNameForMailcamp(name: string): string {
  const trimmed = name.trim()
  return trimmed ? `\u00A0${trimmed},` : ','
}

// The zone our records are dated in. Fixed, not the server's or the viewer's.
export const BUSINESS_TIME_ZONE = 'Europe/Amsterdam'

// An instant as `YYYY-MM-DD HH:mm:ss` in BUSINESS_TIME_ZONE; the date part is the Dutch day.
// Only for timestamps; a stored plain date like a birthday must not go through it.
export function businessDateTimeFromInstant(instant: Date): string {
  return instant.toLocaleString('sv-SE', { timeZone: BUSINESS_TIME_ZONE })
}
